import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { FiAlertTriangle, FiInfo } from "react-icons/fi";
import LoadingOverlay from '../shared/LoadingOverlay';
import { getAccessToken } from '../../services/authApi';
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import elliemaeApi from '../../services/interceptors/elliemaeApi';

dayjs.extend(utc);
dayjs.extend(timezone);

const API_DOMAIN = process.env.REACT_APP_TPOC_API_DOMAIN;

const LoanComparison = () => {
    const [loanId, setLoanId] = useState(null);
    const [fields, setFields] = useState([]);
    const [lockStatus, setLockStatus] = useState('');
    const [lockDate, setLockDate] = useState('');
    const [lockExpire, setLockExpire] = useState('');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [snapshotDates, setSnapshotDates] = useState({
        locked: null,
        disclosed: null,
        initialApproval: null,
        conditionalApproval: null,
        ctc: null
    });


    useEffect(() => {
        const init = async () => {
            setLoading(true);
            setError(null);

            try {
                // Initialize loan object
                if (window.elli && window.elli.script && window.elli.script.guest) {
                    window.elli.script.guest.create();
                }
                const loanObject = await window.elli.script.getObject("loan");
                const loan = await loanObject.all();
                setLoanId(loan?.loan?.id);

                const authObject = await window.elli.script.getObject("auth");
                let authCode = await authObject.createAuthCode();
                const accessToken = await getAccessToken(authCode);

                // Lock comparison API
                let lockRes;
                try {
                    lockRes = await elliemaeApi.get(
                        `/secondary/v1/loans/${loan?.loan?.id}/lockComparison`,
                        {
                            headers: {
                                Authorization: `Bearer ${accessToken}`,
                                "Content-Type": "application/json",
                            },
                        }
                    );
                } catch (err) {
                    lockRes = await handleLockComparisonError(
                        err,
                        loan?.loan?.id,
                        authObject
                    );
                }
                const {
                    lockDate,
                    lockExpirationDate,
                    lockValidationStatus,
                    fields: lockFields,
                } = lockRes?.data ? lockRes?.data : lockRes;

                // Second API (optional: if fails, just continue with lockRes)
                let loanRes = { data: {} };
                try {
                    loanRes = await axios.get(
                        `${API_DOMAIN}/api/loan/${loan?.loan?.id}/loancomparison`,
                        {
                            params: {
                                authCode: authCode,
                                resourceId: loan?.loan?.id,
                            },
                            headers: {
                                "Content-Type": "application/json",
                            },
                        }
                    );
                } catch (err) {
                    console.warn("Loan comparison failed, rendering only lock data:", err);
                }

                const {
                    disclosedCreatedDate,
                    initialApprovalCreatedDate,
                    conditionalApprovalCreatedDate,
                    ctcCreatedDate,
                    comparisons: loanFields = [],
                } = loanRes.data || {};

                // Merge lock fields with loan comparison if available
                const combinedFields = lockFields.map(lockField => {
                    const match = loanFields.find(lf => lf.fieldId === lockField.loanFieldId);
                    return {
                        ...lockField,
                        disclosedValue: match?.disclosedValue || "",
                        initialApprovalValue: match?.initialApprovalValue || "",
                        conditionalApprovalValue: match?.conditionalApprovalValue || "",
                        ctcValue: match?.ctcValue || ""
                    };
                });

                setFields(combinedFields);
                setLockStatus(lockValidationStatus);
                setLockDate(lockDate);
                setLockExpire(lockExpirationDate);

                setSnapshotDates({
                    locked: lockDate,
                    disclosed: disclosedCreatedDate,
                    initialApproval: initialApprovalCreatedDate,
                    conditionalApproval: conditionalApprovalCreatedDate,
                    ctc: ctcCreatedDate
                });
            } catch (err) {
                console.error("Unexpected error:", err);
                setError("An unexpected error occurred.");
            } finally {
                setLoading(false);
            }
        };

        init();
    }, []);

    const handleLockComparisonError = async (err, loanId, authObject) => {
        console.error("Lock comparison failed:", err);

        const errCode = err?.response?.data?.code;
        const errSummary = err?.response?.data?.errors?.[0]?.summary;

        // Generate a fresh auth code and new api call for getting current loan data
        let authCode = await authObject.createAuthCode();
        if (errCode?.toLowerCase() === "smos-002" || errSummary?.toLowerCase().contains('no current lock found')) {
            try {
                const currentLoanRes = await axios.get(
                    `${API_DOMAIN}/api/loan/${loanId}/${authCode}/currentloandata`,
                    { headers: { "Content-Type": "application/json" } }
                );
                return currentLoanRes?.data;
            } catch (apiErr) {
                console.error("CurrentLoanData API failed:", apiErr);
                setError("CurrentLoanData API failed", apiErr);
            }
        }
        else {
            console.error("Elliemae api failed");
            setError(`Elliemae api failed Error Code: ${errCode}, Summary: ${errSummary}`)
        }
        // // If lock not found → show your custom UI message
        // if (errCode?.toLowerCase() === "smos-002" || errSummary?.toLowerCase().contains('no current lock found')) {
        //     setFields([]);
        //     setError("You cannot continue because the loan is not locked. Please lock the loan and try again.");
        //     return;
        // }

        // // For all other errors → generic message
        // setError("An unexpected error occurred. Please try again later.");
        // return;
    };

    // Count of fields with differences
    const diffCount = fields.filter(
        f => (f.currentLoanValue ?? '').toString().trim() !== (f.currentLockValue ?? '').toString().trim()
    ).length;

    return (
        <>
            <LoadingOverlay loading={loading} text="Loading..." />

            {/* Error block */}
            {error && (
                <div className="flex items-center justify-center min-h-screen bg-gray-50 p-6">
                    <div className="bg-white shadow-md rounded-lg p-8 max-w-lg text-center border border-gray-200 relative -mt-20">
                        <div className="flex justify-center mb-4">
                            <FiInfo size={48} className="text-blue-500" />
                        </div>
                        <h2 className="text-xl font-semibold text-gray-800 mb-3"></h2>
                        <p className="text-gray-600">{error}</p>
                    </div>
                </div>
            )}

            {/* Table renders only if no error and not loading */}
            {!error && !loading && (
                <div className="space-y-10 p-6">
                    <div className="bg-white rounded-lg p-3 space-y-6 max-w-8xl mx-auto">
                        <h2 className="page-title">Loan Comparison</h2>

                        <div className="flex items-center justify-between pb-2 flex-wrap gap-4">
                            <div className="flex flex-wrap justify-start items-center gap-4 text-sm text-left">
                                <p>Lock Date: {lockDate || '-'}</p>
                                <p>Expiration: {lockExpire || '-'}</p>
                                {lockStatus && (
                                    <p className="text-red-600 flex items-center gap-1 w-full md:w-auto justify-start">
                                        <FiAlertTriangle size={20} className="text-orange-600" />
                                        {lockStatus}
                                    </p>
                                )}
                            </div>
                        </div>

                        <div className="overflow-x-auto">
                            <table className="w-full text-sm border-collapse mt-2">
                                <thead>
                                    <tr>
                                        <th>Field Name</th>
                                        <th>
                                            Locked <br />
                                            <span className="text-xs text-gray-500">
                                                {snapshotDates.locked
                                                    ? dayjs(snapshotDates.locked).format("YYYY-MM-DD")
                                                    : '-'}
                                            </span>
                                        </th>

                                        <th>
                                            Disclosed <br />
                                            <span className="text-xs text-gray-500">
                                                {snapshotDates.disclosed
                                                    ? `${dayjs.tz(snapshotDates.disclosed, "America/Los_Angeles").format("YYYY-MM-DD HH:mm:ss")} PT`
                                                    : '-'}
                                            </span>
                                        </th>

                                        <th>
                                            Initial Approval <br />
                                            <span className="text-xs text-gray-500">
                                                {snapshotDates.initialApproval
                                                    ? `${dayjs.tz(snapshotDates.initialApproval, "America/Los_Angeles").format("YYYY-MM-DD HH:mm:ss")} PT`
                                                    : '-'}
                                            </span>
                                        </th>

                                        <th>
                                            Conditional Approval <br />
                                            <span className="text-xs text-gray-500">
                                                {snapshotDates.conditionalApproval
                                                    ? `${dayjs.tz(snapshotDates.conditionalApproval, "America/Los_Angeles").format("YYYY-MM-DD HH:mm:ss")} PT`
                                                    : '-'}
                                            </span>
                                        </th>

                                        <th>
                                            CTC <br />
                                            <span className="text-xs text-gray-500">
                                                {snapshotDates.ctc
                                                    ? `${dayjs.tz(snapshotDates.ctc, "America/Los_Angeles").format("YYYY-MM-DD HH:mm:ss")} PT`
                                                    : '-'}
                                            </span>
                                        </th>

                                        <th>Current</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {fields.map(f => {
                                        const normalize = (val) => {
                                            if (val === null || val === undefined) return "";
                                            const str = val.toString().trim().toLowerCase();
                                            if (str === "true") return "y";
                                            if (str === "false") return "n";
                                            return !isNaN(str) && str !== "" ? parseFloat(str).toString() : str;
                                        };

                                        let isDifferent = false;

                                        if (f.loanFieldId === "420") {
                                            isDifferent = f.comparisonStatus?.toLowerCase() !== "same";
                                        } else {
                                            const current = normalize(f.currentLoanValue);
                                            const milestoneValues = [
                                                f.currentLockValue,
                                                f.disclosedValue,
                                                f.initialApprovalValue,
                                                f.conditionalApprovalValue,
                                                f.ctcValue
                                            ].map(normalize).filter(v => v !== "");
                                            if (milestoneValues.length === 0) {
                                                isDifferent = false;
                                            } else {
                                                if (milestoneValues.length > 0) {
                                                    const valueMismatch = milestoneValues.some(v => v && v !== current);
                                                    const allPreviousEmpty = current && milestoneValues.every(v => !v);
                                                    const previousHasValue = !current && milestoneValues.some(v => v);
                                                    const mixedValues = current && milestoneValues.some(v => v === current) && milestoneValues.some(v => !v || v !== current);
                                                    isDifferent = valueMismatch || allPreviousEmpty || previousHasValue || mixedValues;
                                                }
                                            }
                                        }

                                        return (
                                            <tr key={f.lockFieldId} className={isDifferent ? "bg-red-100 font-semibold text-red-700" : ""}>
                                                <td className="p-2 border font-medium">{f.description}</td>
                                                <td className="p-2 border">{f.currentLockValue || '-'}</td>
                                                <td className="p-2 border">{f.disclosedValue || '-'}</td>
                                                <td className="p-2 border">{f.initialApprovalValue || '-'}</td>
                                                <td className="p-2 border">{f.conditionalApprovalValue || '-'}</td>
                                                <td className="p-2 border">{f.ctcValue || '-'}</td>
                                                <td className="p-2 border">{f.currentLoanValue || '-'}</td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default LoanComparison;
